import * as THREE from "three";
import { createEngine, type Engine } from "./engine";
import { createGameLoop } from "./game-loop";
import { createInputManager, type InputManager } from "./input";
import {
  CHUNK_SIZE,
  SEA_LEVEL,
  SPAWN_POINT,
  PLAYER_EYE_HEIGHT,
  BLOCK_BREAK_REACH,
  WORLD_SEED_DEFAULT,
  RENDER_DISTANCE,
  getBlockIndex,
  clampHpToZero,
  computeXpPenalty,
  createBlockOverrides,
} from "@craftgaming/shared";
import { generateChunkTerrain } from "@craftgaming/shared";
import { BlockType, isBlockTransparent } from "@craftgaming/shared";
import type { ClassDef, GameMode } from "@craftgaming/shared";
import { BASE_MELEE_DAMAGE } from "@craftgaming/shared";
import { createPlayerController } from "../systems/player-controller";
import {
  raycastVoxels,
  getCameraDirection,
  createInventory,
  getBlockDrop,
  createProgressionState,
  addXp,
  XP_REWARDS,
  createHungerState,
  updateHunger,
  addFood,
  FOOD_DEFS,
  SPRINT_MIN_HUNGER,
  computeRegen,
  lerp,
  dampFactor,
  getMeleeDamage,
  TOOL_DEFS,
  createQuestState,
  deserializeQuestState,
  serializeQuestState,
  questEvent,
  type QuestEventType,
  isPlaceableBlock,
  craft,
  getDayNight,
  computeAbilityHits,
  type Recipe,
  type AbilityTarget,
  type WorldBlockAccess,
} from "@craftgaming/shared";
import { createBlockHighlight } from "../rendering/block-highlight";
import { createBlockInteraction } from "../systems/block-interaction";
import { createChunkLoader } from "../systems/chunk-loader";
import { createHotbar } from "../ui/hotbar";
import { createAbilityBar } from "../ui/ability-bar";
import { createAmbientMusic } from "../audio/ambient-music";
import { createPauseMenu } from "../ui/pause-menu";
import { createXpBar } from "../ui/xp-bar";
import { createStatusBars } from "../ui/status-bars";
import { createInventoryScreen } from "../ui/inventory-screen";
import { createDeathScreen } from "../ui/death-screen";
import { createDamageOverlay } from "../ui/damage-overlay";
import { getTextureAtlas } from "../rendering/texture-atlas";
import { createAbilityEffects } from "../rendering/ability-effects";
import { createMobSystem, damageMob, getMobStats } from "../systems/mob-system";
import { createMobRenderer } from "../rendering/mob-renderer";
import { createRemotePlayers } from "../systems/remote-players";
import type { NetworkSession } from "../network/colyseus-network";
import { showConnectionBanner } from "../ui/connection-banner";
import { createCameraRig } from "../systems/camera-rig";
import { createFirstPersonArm } from "../rendering/first-person-arm";
import { createPlayerModel, colorsFromClassColor, DEFAULT_COLORS } from "../rendering/player-model";
import { createBlockParticles } from "../rendering/block-particles";
import { createSfx } from "../audio/sfx";
import { createMinimap } from "../ui/minimap";
import { createChat } from "../ui/chat";
import { createTouchControls, isTouchDevice, type TouchControls } from "../ui/touch-controls";
import { createControlsHint, type ControlsHint } from "../ui/controls-hint";
import { createMobArrows } from "../systems/mob-arrows";
import { createDroppedItems } from "../systems/dropped-items";
import { createBlockCracks } from "../rendering/block-cracks";
import { createQuestPanel } from "../ui/quest-panel";
import type { MobEntity } from "../systems/mob-system";
import { createChestStore } from "../systems/chest-store";
import { createChestScreen } from "../ui/chest-screen";

export type GameOptions = {
  classDef: ClassDef;
  playerName: string;
  /** Connected session for multiplayer; null/undefined = single-player. */
  network?: NetworkSession | null;
  /** World seed (authoritative from server when online). */
  seed?: number;
  /** survival (default) or creative. */
  gameMode?: GameMode;
};

export type Game = {
  start: () => void;
  stop: () => void;
  dispose: () => void;
};

export function createGame(canvas: HTMLCanvasElement, options: GameOptions): Game {
  const engine: Engine = createEngine(canvas);
  const input: InputManager = createInputManager(canvas);
  const atlas = getTextureAtlas();

  const network = options.network ?? null;
  const worldSeed = options.seed ?? WORLD_SEED_DEFAULT;
  const creative = options.gameMode === "creative";

  // Player-configurable render distance (persisted)
  const savedRd = parseInt(localStorage.getItem("craftgaming_renderdist") ?? "", 10);
  let renderDistance = Number.isFinite(savedRd) && savedRd >= 4 && savedRd <= 10
    ? savedRd
    : RENDER_DISTANCE;

  // Chunk storage — solid geometry and translucent water are separate
  // meshes (water renders Minecraft-style: see-through, no inner faces)
  type ChunkEntry = {
    mesh: THREE.Mesh | null;
    waterMesh: THREE.Mesh | null;
    blocks: Uint16Array;
  };
  const chunks = new Map<string, ChunkEntry>();

  // Declared before loadInitialChunks() runs (function declarations are
  // hoisted, consts are not — TDZ crash otherwise)
  const NEIGHBOR_DIRS: Array<[number, number, number]> = [
    [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
  ];

  // Shared chunk materials (never disposed per-chunk)
  const solidMaterial = new THREE.MeshLambertMaterial({
    map: atlas.texture,
    vertexColors: true,
  });
  const waterMaterial = new THREE.MeshLambertMaterial({
    map: atlas.texture,
    vertexColors: true,
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
    side: THREE.DoubleSide, // surface visible from below too
  });

  // Block edits on top of deterministic terrain — reapplied whenever a
  // chunk is (re)generated. Fed by local edits and server broadcasts.
  const blockOverrides = createBlockOverrides();

  // World block access adapter
  const worldAccess: WorldBlockAccess = {
    getBlockAtWorld(wx: number, wy: number, wz: number): BlockType {
      const cx = Math.floor(wx / CHUNK_SIZE);
      const cy = Math.floor(wy / CHUNK_SIZE);
      const cz = Math.floor(wz / CHUNK_SIZE);
      const key = `${cx},${cy},${cz}`;
      const chunk = chunks.get(key);
      if (!chunk) return BlockType.AIR;
      const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
      const ly = ((wy % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
      const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
      return chunk.blocks[getBlockIndex(lx, ly, lz)] as BlockType;
    },
  };

  // Player controller — spawn height is corrected to ground level after
  // the initial chunks load (no more falling from the sky on join)
  const spawnY = SEA_LEVEL + 20;
  const playerController = createPlayerController(0, spawnY, 0, worldAccess, {
    creative,
    // Too hungry = too tired to sprint
    canSprint: () => creative || hungerState.hunger > SPRINT_MIN_HUNGER,
  });

  /** Top of the first non-air block at (wx, wz) — water counts as ground
   *  so ocean spawns float on the surface instead of sinking. */
  function findGroundY(wx: number, wz: number): number {
    for (let y = CHUNK_SIZE * 6 - 1; y > 0; y--) {
      if (worldAccess.getBlockAtWorld(wx, y, wz) !== BlockType.AIR) return y + 1;
    }
    return SEA_LEVEL + 1;
  }

  // Personal respawn point — set by sleeping in a bed.
  // Scoped by world seed: a bed from another world must not apply here.
  const BED_SPAWN_KEY = `craftgaming_bedspawn:${worldSeed}`;
  let bedSpawn: { x: number; z: number } | null = null;
  try {
    const saved = JSON.parse(localStorage.getItem(BED_SPAWN_KEY) ?? "null");
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.z)) {
      bedSpawn = { x: saved.x, z: saved.z };
    }
  } catch {
    bedSpawn = null;
  }

  // Block highlight
  const blockHighlight = createBlockHighlight(engine.scene);

  // Inventory + progression + hunger
  // 9 hotbar slots + 27 backpack = 36 (Minecraft). Chests hold more.
  const inventory = createInventory(36);
  const progression = createProgressionState();
  const hungerState = createHungerState();

  // Creative starts stocked with everything usable — every buildable
  // block (never WATER/BEDROCK, unbreakable = permanent grief) plus one
  // of every tool, so the player can use the whole kit.
  if (creative) {
    for (const id of Object.values(BlockType)) {
      if (typeof id !== "number") continue;
      if (!isPlaceableBlock(id)) continue;
      if (id === BlockType.WATER || id === BlockType.BEDROCK) continue;
      inventory.addItem(id, 64);
    }
    for (const toolId of Object.keys(TOOL_DEFS)) {
      inventory.addItem(Number(toolId), 1);
    }
  }

  // UI (hotbar reads from inventory — starts empty).
  // Wheel/number keys are disabled while any overlay is open.
  const hotbar = createHotbar(inventory, {
    isUiOpen: () => inventoryOpen || chestOpen || paused || isDead || chat.isOpen(),
  });
  const abilityBar = createAbilityBar(options.classDef);
  const statusBars = createStatusBars();
  const xpBar = createXpBar();
  xpBar.update(progression);

  function grantXp(amount: number) {
    const events = addXp(progression, amount);
    xpBar.update(progression);
    for (const ev of events) {
      xpBar.showLevelUp(ev.newLevel);
      sfx.playLevelUp();
    }
  }

  // ─── Quests (guided progression with automatic rewards) ───
  const QUEST_SAVE_KEY = `craftgaming_quests:${worldSeed}`;
  const questState = deserializeQuestState(localStorage.getItem(QUEST_SAVE_KEY));
  const questPanel = createQuestPanel(() => questState);

  function fireQuestEvent(type: QuestEventType, targetId?: number, amount = 1) {
    const completed = questEvent(questState, type, targetId, amount);
    localStorage.setItem(QUEST_SAVE_KEY, serializeQuestState(questState));
    questPanel.refresh(questState);
    if (!completed) return;

    // Grant rewards — anything that doesn't fit drops at the player's feet
    for (const item of completed.reward.items) {
      if (inventory.canFit(item.id, item.count)) {
        inventory.addItem(item.id, item.count);
      } else {
        const ps = playerController.state;
        droppedItems.spawn(Math.floor(ps.x), Math.floor(ps.y), Math.floor(ps.z), item.id, item.count);
      }
    }
    grantXp(completed.reward.xp);
    hotbar.refresh();
    questPanel.showCompletion(completed);
    sfx.playLevelUp();
  }

  function sleepInBed() {
    const ps = playerController.state;
    bedSpawn = { x: ps.x, z: ps.z };
    localStorage.setItem(BED_SPAWN_KEY, JSON.stringify(bedSpawn));

    if (network?.isConnected()) {
      chat.addMessage({
        username: "",
        text: "🛏 Ponto de renascimento definido! (a noite só passa quando todos dormem)",
        system: true,
      });
      return;
    }

    // Single-player: sleeping skips the night
    const isNight = getDayNight(localTimeOfDay).isNight;
    if (isNight) {
      localTimeOfDay = 0.05; // morning
      chat.addMessage({ username: "", text: "🛏 Você dormiu — bom dia!", system: true });
    } else {
      chat.addMessage({
        username: "",
        text: "🛏 Ponto de renascimento definido (só dá para dormir à noite)",
        system: true,
      });
    }
  }

  function handleMobKilled(mob: MobEntity) {
    const stats = getMobStats(mob.type);
    grantXp(stats.xpDrop);
    // Animals drop meat on the ground (walk over to collect)
    if (stats.drop) {
      const count = stats.drop.min + Math.floor(Math.random() * (stats.drop.max - stats.drop.min + 1));
      droppedItems.spawn(Math.floor(mob.x), Math.floor(mob.y), Math.floor(mob.z), stats.drop.itemId, count);
    }
    if (!stats.passive) fireQuestEvent("kill", mob.type);
  }

  // Ability visual effects
  const abilityEffects = createAbilityEffects(engine.scene);

  // Mob system
  const mobSystem = createMobSystem(worldAccess);
  const mobRenderer = createMobRenderer(engine.scene);

  // Sound effects (procedural Web Audio)
  const sfx = createSfx();

  // Block break particles
  const blockParticles = createBlockParticles(engine.scene);

  // Skeleton arrows (visible ranged attacks)
  const mobArrows = createMobArrows(engine.scene, worldAccess);

  // Dropped item entities (broken blocks drop pickups)
  const droppedItems = createDroppedItems(engine.scene, worldAccess);

  // Breaking cracks overlay (Minecraft-style)
  const blockCracks = createBlockCracks(engine.scene);

  // Camera rig: F5 cycles 1st person → 3rd back → 3rd front
  const cameraRig = createCameraRig(worldAccess);

  // First-person arm + local third-person body — class color, the SAME
  // outfit remote players see (championClass synced via schema)
  const classColor = options.classDef.color;
  const firstPersonArm = createFirstPersonArm(engine.camera, engine.scene, {
    skin: DEFAULT_COLORS.skin,
    sleeve: classColor,
  });
  const localModel = createPlayerModel(colorsFromClassColor(classColor));
  localModel.group.visible = false;
  engine.scene.add(localModel.group);

  // Minimap (direct chunk access — cheaper than per-block world lookups)
  const minimap = createMinimap({
    getChunkBlocks: (cx, cy, cz) => chunks.get(`${cx},${cy},${cz}`)?.blocks,
  });

  // Touch controls (mobile)
  const touch = isTouchDevice();
  let touchControls: TouchControls | null = null;
  if (touch) {
    touchControls = createTouchControls(input.state);
  }

  // Controls guide (F1 toggles; auto-hides) — touch has visible buttons
  let controlsHint: ControlsHint | null = null;
  if (!touch) {
    controlsHint = createControlsHint();
  }

  // Day/night — server-synced when online, local cycle offline
  let localTimeOfDay = 0.2; // morning
  const OFFLINE_DAY_LENGTH = 1200; // seconds, matches server (20 min)

  // Damage overlay (flash + vignette)
  const damageOverlay = createDamageOverlay();

  // Death state
  let isDead = false;

  // Death screen
  const deathScreen = createDeathScreen({
    onRespawn: () => respawn(),
  });

  function applyDamage(amount: number) {
    if (creative) return; // invulnerable
    if (isDead || amount <= 0) return;
    abilityBar.state.health = clampHpToZero(abilityBar.state.health - amount);
    damageOverlay.triggerFlash();
    damageOverlay.updateVignette(abilityBar.state.health);
    statusBars.updateHealth(abilityBar.state.health, abilityBar.state.maxHealth);
    if (abilityBar.state.health <= 0) triggerDeath();
  }

  function triggerDeath() {
    isDead = true;
    const xpLost = progression.xp;
    computeXpPenalty(progression);
    xpBar.update(progression);
    document.exitPointerLock();
    deathScreen.show(xpLost);
  }

  function respawn() {
    // Reset player state
    isDead = false;

    // Clear inventory
    for (let i = 0; i < inventory.items.length; i++) {
      inventory.items[i] = null;
    }
    hotbar.refresh();

    // Restore HP and hunger
    abilityBar.state.health = abilityBar.state.maxHealth;
    hungerState.hunger = hungerState.maxHunger;
    hungerState.saturation = 5;

    // Teleport to spawn (bed if set) — chunks must be loaded BEFORE
    // probing the ground (dying far away may have unloaded the area)
    const spawnX = bedSpawn?.x ?? SPAWN_POINT.x;
    const spawnZ = bedSpawn?.z ?? SPAWN_POINT.z;
    loadChunksAround(spawnX, spawnZ);
    playerController.state.x = spawnX;
    playerController.state.z = spawnZ;
    playerController.state.y = findGroundY(Math.floor(spawnX), Math.floor(spawnZ));
    playerController.state.vx = 0;
    playerController.state.vy = 0;
    playerController.state.vz = 0;
    playerController.state.fallStartY = null;

    // Update UI
    statusBars.updateHealth(abilityBar.state.health, abilityBar.state.maxHealth);
    statusBars.updateHunger(hungerState.hunger, hungerState.maxHunger);
    damageOverlay.updateVignette(abilityBar.state.health);
    xpBar.update(progression);

    deathScreen.hide();
    canvas.requestPointerLock();
  }

  // Chest storage (Minecraft logic: 9-slot hotbar + chests for the rest)
  const chestStore = createChestStore();
  let chestOpen = false;
  const chestScreen = createChestScreen({
    onShow: () => {
      chestOpen = true;
      document.exitPointerLock();
    },
    onHide: () => {
      chestOpen = false;
      overlayClosedAt = performance.now();
      if (!touch && !skipNextRelock) canvas.requestPointerLock();
      skipNextRelock = false;
    },
    onChanged: () => hotbar.refresh(),
  });

  // Inventory screen (E key) with crafting
  let inventoryOpen = false;
  // Closing an overlay with ESC must NOT relock the pointer in the same
  // keypress (the browser unlocks again from that very ESC and the
  // pointerlockchange handler would open the PAUSE menu). We skip the
  // relock and briefly suppress auto-pause instead.
  let skipNextRelock = false;
  let overlayClosedAt = 0;
  const inventoryScreen = createInventoryScreen(inventory, options.classDef, {
    onShow: () => {
      inventoryOpen = true;
      document.exitPointerLock();
    },
    onHide: () => {
      inventoryOpen = false;
      overlayClosedAt = performance.now();
      if (!touch && !skipNextRelock) {
        canvas.requestPointerLock();
      }
      skipNextRelock = false;
    },
    onCraft: (recipe: Recipe) => {
      const ok = craft(inventory, recipe, isNearBlock(BlockType.CRAFTING_TABLE), isNearBlock(BlockType.FURNACE));
      if (ok) {
        sfx.playCraft();
        grantXp(XP_REWARDS.craftItem);
        fireQuestEvent("craft", recipe.output.id, recipe.output.count);
        hotbar.refresh();
      }
      return ok;
    },
    isNearTable: () => isNearBlock(BlockType.CRAFTING_TABLE),
    isNearFurnace: () => isNearBlock(BlockType.FURNACE),
    onChanged: () => hotbar.refresh(),
    getSelectedHotbar: () => hotbar.selectedIndex,
    setSelectedHotbar: (index: number) => { hotbar.selectedIndex = index; },
  });

  /** A given station block within a 4-block radius (Minecraft-style). */
  function isNearBlock(target: BlockType): boolean {
    if (creative) return true;
    const ps = playerController.state;
    const px = Math.floor(ps.x);
    const py = Math.floor(ps.y);
    const pz = Math.floor(ps.z);
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        for (let dz = -4; dz <= 4; dz++) {
          if (worldAccess.getBlockAtWorld(px + dx, py + dy, pz + dz) === target) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Init status bars
  statusBars.updateHealth(abilityBar.state.health, abilityBar.state.maxHealth);
  statusBars.updateHunger(hungerState.hunger, hungerState.maxHunger);

  // Pause menu
  let paused = false;
  const pauseMenu = createPauseMenu({
    onResume: () => {
      paused = false;
      canvas.requestPointerLock();
    },
    onVolumeChange: (volume) => {
      music.setVolume(volume);
      sfx.setVolume(volume * 0.8);
    },
    onSensitivityChange: (_sensitivity) => {
      // Could pipe to player controller later
    },
    onRenderDistanceChange: (chunks) => {
      renderDistance = chunks;
      localStorage.setItem("craftgaming_renderdist", String(chunks));
      chunkLoader.forceRecalculate();
    },
    initialRenderDistance: renderDistance,
  });

  // Block interaction with inventory/XP
  const blockInteraction = createBlockInteraction({
    // Food and other non-block items are not placeable
    getSelectedBlock: () => {
      const selected = hotbar.getSelectedBlock();
      return isPlaceableBlock(selected) ? (selected as BlockType) : BlockType.AIR;
    },
    getHeldItem: () => hotbar.getSelectedBlock(),
    getChunkBlocks: (cx, cy, cz) => chunks.get(`${cx},${cy},${cz}`)?.blocks,
    updateChunkBlock: (wx, wy, wz, blockId) => {
      const oldBlock = setBlockLocal(wx, wy, wz, blockId);
      if (oldBlock === null) return;

      // Remember the edit so chunk unload/reload doesn't revert it.
      blockOverrides.set(wx, wy, wz, blockId);

      const isBreaking = blockId === BlockType.AIR && oldBlock !== BlockType.AIR;
      const isPlacing = blockId !== BlockType.AIR;

      if (isBreaking) {
        // Breaking a chest dumps its contents on the ground first
        if (oldBlock === BlockType.CHEST) {
          for (const stored of chestStore.removeChest(wx, wy, wz)) {
            droppedItems.spawn(wx, wy, wz, stored.blockId, stored.count);
          }
        }
        // Survival: the block drops as a pickup entity on the ground
        // (walk over it to collect). Creative: no drops.
        if (!creative) {
          const drop = getBlockDrop(oldBlock);
          if (drop) droppedItems.spawn(wx, wy, wz, drop.blockId, drop.count);
        }
        grantXp(XP_REWARDS.breakBlock);
        fireQuestEvent("break", oldBlock);
        blockParticles.spawnBreak(wx, wy, wz, oldBlock);
        sfx.playBreak();
        network?.sendBlockBreak(wx, wy, wz);
      }

      if (isPlacing) {
        // Creative has infinite blocks — survival consumes them
        if (!creative) {
          inventory.removeItem(blockId, 1);
          hotbar.refresh();
        }
        grantXp(XP_REWARDS.placeBlock);
        fireQuestEvent("place", blockId);
        sfx.playPlace();
        firstPersonArm.swing();
        localModel.swingArm();
        network?.sendBlockPlace(wx, wy, wz, blockId);
      }
    },
    getPlayerPosition: () => ({
      x: playerController.state.x,
      y: playerController.state.y,
      z: playerController.state.z,
    }),
    isCreative: () => creative,
  });

  // Ambient music
  const music = createAmbientMusic();

  // Dynamic chunk loading
  const chunkLoader = createChunkLoader({
    loadChunk: (cx, cy, cz) => loadChunk(cx, cy, cz),
    unloadChunk: (cx, cy, cz) => unloadChunk(cx, cy, cz),
    isChunkLoaded: (cx, cy, cz) => chunks.has(`${cx},${cy},${cz}`),
    getRenderDistance: () => renderDistance,
  });

  // Chat (Enter opens; also shows join/leave notifications)
  const chat = createChat({
    onSend: (text) => {
      if (network?.isConnected()) {
        network.sendChat(text);
      } else {
        // Offline: local echo so the chat still feels alive
        chat.addMessage({ username: options.playerName, text });
      }
    },
    canOpen: () => !paused && !inventoryOpen && !chestOpen && !isDead,
    onClose: () => {
      if (!touch && !paused && !inventoryOpen && !isDead) {
        canvas.requestPointerLock();
      }
    },
  });

  // Attack edge detection
  let prevAttack = false;
  let prevInteract = false;
  let armSwingCooldown = 0;

  // Debug overlay + crosshair (for aim feedback)
  const debugEl = document.getElementById("debug-overlay");
  const crosshairEl = document.getElementById("crosshair");
  let frameCount = 0;
  let fpsTimer = 0;
  let currentFps = 0;

  // Pause via pointer lock loss (ESC exits pointer lock natively).
  // Touch devices have no pointer lock — never auto-pause there.
  function handlePointerLockChange() {
    if (touch) return;
    const locked = document.pointerLockElement === canvas;
    // Grace window right after closing an overlay: the same ESC that
    // closed it can still bounce the lock — that's not a pause request.
    if (performance.now() - overlayClosedAt < 1200) return;
    if (!locked && !paused && !inventoryOpen && !chestOpen && !isDead && !chat.isOpen()) {
      paused = true;
      pauseMenu.show();
    }
  }
  document.addEventListener("pointerlockchange", handlePointerLockChange);

  // E toggles the inventory; ESC closes overlay layers first (recipe
  // detail → inventory) — the pointer lock is already released while the
  // inventory is open, so this ESC never reaches the pause flow.
  function handleUiKeys(e: KeyboardEvent) {
    if (e.code === "KeyE") {
      if (chestOpen) {
        chestScreen.hide();
      } else if (inventoryOpen) {
        inventoryScreen.hide();
      } else if (!paused && !isDead && !chat.isOpen()) {
        inventoryScreen.show();
      }
    }

    if (e.code === "Escape" && (inventoryOpen || chestOpen)) {
      e.preventDefault();
      skipNextRelock = true; // relocking on this same ESC would bounce into pause
      if (chestOpen) {
        chestScreen.hide();
      } else if (inventoryScreen.isDetailOpen()) {
        skipNextRelock = false; // detail close keeps the screen open — no relock happens
        inventoryScreen.closeDetail();
      } else {
        inventoryScreen.hide();
      }
    }

    // Q = drop one of the held item, thrown ~2 blocks ahead (Minecraft)
    if (e.code === "KeyQ" && !paused && !inventoryOpen && !chestOpen && !isDead && !chat.isOpen()) {
      const selected = hotbar.getSelectedBlock();
      if (selected !== BlockType.AIR && inventory.getCount(selected) > 0) {
        inventory.removeItem(selected, 1);
        hotbar.refresh();
        const ps = playerController.state;
        const dir = getCameraDirection(ps.yaw, ps.pitch);
        droppedItems.spawn(
          Math.floor(ps.x + dir.x * 2),
          Math.floor(ps.y + 1),
          Math.floor(ps.z + dir.z * 2),
          selected,
          1
        );
        sfx.playPlace();
      }
    }
  }
  document.addEventListener("keydown", handleUiKeys);

  // ─── Face definitions for chunk mesh builder ───

  type FaceInfo = {
    dir: number[];
    normal: number[];
    verts: number[][];
    faceType: "top" | "bottom" | "side";
    uvs: number[][];
  };

  const faces: FaceInfo[] = [
    { dir: [0, 1, 0], normal: [0, 1, 0], verts: [[0,1,1],[1,1,1],[1,1,0],[0,1,0]], faceType: "top", uvs: [[0,0],[1,0],[1,1],[0,1]] },
    { dir: [0,-1, 0], normal: [0,-1, 0], verts: [[0,0,0],[1,0,0],[1,0,1],[0,0,1]], faceType: "bottom", uvs: [[0,0],[1,0],[1,1],[0,1]] },
    { dir: [0, 0, 1], normal: [0, 0, 1], verts: [[0,0,1],[1,0,1],[1,1,1],[0,1,1]], faceType: "side", uvs: [[0,1],[1,1],[1,0],[0,0]] },
    { dir: [0, 0,-1], normal: [0, 0,-1], verts: [[1,0,0],[0,0,0],[0,1,0],[1,1,0]], faceType: "side", uvs: [[0,1],[1,1],[1,0],[0,0]] },
    { dir: [1, 0, 0], normal: [1, 0, 0], verts: [[1,0,1],[1,0,0],[1,1,0],[1,1,1]], faceType: "side", uvs: [[0,1],[1,1],[1,0],[0,0]] },
    { dir: [-1,0, 0], normal: [-1,0, 0], verts: [[0,0,0],[0,0,1],[0,1,1],[0,1,0]], faceType: "side", uvs: [[0,1],[1,1],[1,0],[0,0]] },
  ];

  // Remote players (multiplayer)
  const remotePlayers = createRemotePlayers(engine.scene);

  // Bind network handlers BEFORE the initial chunk load so buffered
  // world:overrides land in blockOverrides and get applied on generation.
  if (network) {
    network.bindCallbacks({
      onPlayerAdd: (p) => remotePlayers.add(p),
      onPlayerUpdate: (p) => remotePlayers.updateTarget(p),
      onPlayerRemove: (sessionId) => remotePlayers.remove(sessionId),
      onBlockUpdate: (wx, wy, wz, blockId) => {
        blockOverrides.set(wx, wy, wz, blockId);
        setBlockLocal(wx, wy, wz, blockId); // idempotent for our own echoed edits
      },
      onOverrides: (list) => {
        for (const entry of list) {
          blockOverrides.set(entry.wx, entry.wy, entry.wz, entry.blockId);
          setBlockLocal(entry.wx, entry.wy, entry.wz, entry.blockId);
        }
      },
      onChat: (message) => {
        chat.addMessage({
          username: message.username,
          text: message.text,
          system: message.system,
        });
      },
      onPlayerHit: (from, damage) => {
        // PvP: another player hit us. The server already validated and
        // clamped the damage; apply it to our local health (creative is
        // invulnerable via applyDamage's own guard).
        applyDamage(damage);
        sfx.playHit();
        // Knockback away from the attacker — find their position among the
        // remote players and shove us along the horizontal away-vector.
        const attacker = remotePlayers.getTargets().find((t) => t.sessionId === from);
        if (attacker) {
          const ps = playerController.state;
          const dx = ps.x - attacker.x;
          const dz = ps.z - attacker.z;
          if (dx !== 0 || dz !== 0) playerController.applyKnockback(dx, dz);
        }
      },
      onDisconnect: () => {
        // Degrade explicitly to single-player: frozen avatars pretending
        // to be live players are worse than none.
        remotePlayers.dispose();
        // Keep the sky where the server left it instead of jumping
        localTimeOfDay = network?.getTimeOfDay() ?? localTimeOfDay;
        showConnectionBanner("offline", "conexão perdida durante o jogo");
        chat.addMessage({ username: "", text: "Conexão perdida — modo single-player", system: true });
      },
    });
  }

  // Load initial chunks, then drop the player ON the ground — spawning
  // at a fixed height meant falling from the sky and taking fall damage.
  loadInitialChunks();
  playerController.state.y = findGroundY(0, 0);
  playerController.state.fallStartY = null;

  function loadChunksAround(wx: number, wz: number, radius = 3) {
    const centerCX = Math.floor(wx / CHUNK_SIZE);
    const centerCZ = Math.floor(wz / CHUNK_SIZE);
    for (let cx = centerCX - radius; cx <= centerCX + radius; cx++) {
      for (let cz = centerCZ - radius; cz <= centerCZ + radius; cz++) {
        for (let cy = 0; cy < 6; cy++) {
          loadChunk(cx, cy, cz);
        }
      }
    }
  }

  function loadInitialChunks() {
    loadChunksAround(0, 0);
  }

  /** True if any block on the given border face of the chunk is water. */
  function borderHasWater(blocks: Uint16Array, dx: number, dy: number, dz: number): boolean {
    const fixedAxis = dx !== 0 ? "x" : dy !== 0 ? "y" : "z";
    const fixedValue = (dx || dy || dz) > 0 ? CHUNK_SIZE - 1 : 0;
    for (let a = 0; a < CHUNK_SIZE; a++) {
      for (let b = 0; b < CHUNK_SIZE; b++) {
        const x = fixedAxis === "x" ? fixedValue : a;
        const y = fixedAxis === "y" ? fixedValue : fixedAxis === "x" ? a : b;
        const z = fixedAxis === "z" ? fixedValue : b;
        if (blocks[getBlockIndex(x, y, z)] === BlockType.WATER) return true;
      }
    }
    return false;
  }

  function loadChunk(cx: number, cy: number, cz: number) {
    const key = `${cx},${cy},${cz}`;
    if (chunks.has(key)) return;
    const blocks = generateChunkTerrain(cx, cy, cz, worldSeed);
    blockOverrides.applyToChunk(blocks, cx, cy, cz);
    // Register BEFORE meshing so cross-chunk neighbor checks see it
    const entry: ChunkEntry = { mesh: null, waterMesh: null, blocks };
    chunks.set(key, entry);
    const built = buildChunkMesh(cx, cy, cz, blocks);
    entry.mesh = built.solid;
    entry.waterMesh = built.water;
    if (built.solid) engine.scene.add(built.solid);
    if (built.water) engine.scene.add(built.water);

    // Neighbors meshed before this chunk existed treated this space as
    // AIR and drew water walls against it — remesh the ones that touch
    // water on the shared border (cheap: only water-bearing chunks).
    for (const [dx, dy, dz] of NEIGHBOR_DIRS) {
      if (!chunks.has(`${cx + dx},${cy + dy},${cz + dz}`)) continue;
      if (borderHasWater(blocks, dx, dy, dz)) {
        remeshChunk(cx + dx, cy + dy, cz + dz);
      }
    }
  }

  function disposeChunkMeshes(chunk: ChunkEntry) {
    if (chunk.mesh) {
      engine.scene.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
      chunk.mesh = null;
    }
    if (chunk.waterMesh) {
      engine.scene.remove(chunk.waterMesh);
      chunk.waterMesh.geometry.dispose();
      chunk.waterMesh = null;
    }
  }

  function unloadChunk(cx: number, cy: number, cz: number) {
    const key = `${cx},${cy},${cz}`;
    const chunk = chunks.get(key);
    if (!chunk) return;
    disposeChunkMeshes(chunk);
    chunks.delete(key);
  }

  function remeshChunk(cx: number, cy: number, cz: number) {
    const key = `${cx},${cy},${cz}`;
    const chunk = chunks.get(key);
    if (!chunk) return;
    disposeChunkMeshes(chunk);
    const built = buildChunkMesh(cx, cy, cz, chunk.blocks);
    chunk.mesh = built.solid;
    chunk.waterMesh = built.water;
    if (built.solid) engine.scene.add(built.solid);
    if (built.water) engine.scene.add(built.water);
  }

  /**
   * Writes a block into a loaded chunk and remeshes it (plus border
   * neighbors). Returns the previous block, or null when the chunk isn't
   * loaded or the value is unchanged — which makes applying the server's
   * echo of our own edits a no-op.
   */
  function setBlockLocal(wx: number, wy: number, wz: number, blockId: number): BlockType | null {
    const cx = Math.floor(wx / CHUNK_SIZE);
    const cy = Math.floor(wy / CHUNK_SIZE);
    const cz = Math.floor(wz / CHUNK_SIZE);
    const chunk = chunks.get(`${cx},${cy},${cz}`);
    if (!chunk) return null;

    const lx = ((wx % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const ly = ((wy % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
    const lz = ((wz % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;

    const idx = getBlockIndex(lx, ly, lz);
    const oldBlock = chunk.blocks[idx] as BlockType;
    if (oldBlock === blockId) return null;

    chunk.blocks[idx] = blockId;

    remeshChunk(cx, cy, cz);
    if (lx === 0) remeshChunk(cx - 1, cy, cz);
    if (lx === CHUNK_SIZE - 1) remeshChunk(cx + 1, cy, cz);
    if (ly === 0) remeshChunk(cx, cy - 1, cz);
    if (ly === CHUNK_SIZE - 1) remeshChunk(cx, cy + 1, cz);
    if (lz === 0) remeshChunk(cx, cy, cz - 1);
    if (lz === CHUNK_SIZE - 1) remeshChunk(cx, cy, cz + 1);

    return oldBlock;
  }

  type MeshBuffers = {
    positions: number[];
    normals: number[];
    uvs: number[];
    colors: number[];
    indices: number[];
  };

  function emptyBuffers(): MeshBuffers {
    return { positions: [], normals: [], uvs: [], colors: [], indices: [] };
  }

  function buffersToMesh(buffers: MeshBuffers, material: THREE.Material): THREE.Mesh | null {
    if (buffers.positions.length === 0) return null;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(buffers.positions, 3));
    geometry.setAttribute("normal", new THREE.Float32BufferAttribute(buffers.normals, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(buffers.uvs, 2));
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(buffers.colors, 3));
    geometry.setIndex(buffers.indices);
    return new THREE.Mesh(geometry, material);
  }

  function buildChunkMesh(
    cx: number, cy: number, cz: number,
    blocks: Uint16Array
  ): { solid: THREE.Mesh | null; water: THREE.Mesh | null } {
    const solid = emptyBuffers();
    const water = emptyBuffers();

    // Neighbor lookup that SEES ACROSS chunk borders — sampling only the
    // local buffer painted full water walls on every chunk boundary
    // (the "box inside the water" effect).
    const baseX = cx * CHUNK_SIZE;
    const baseY = cy * CHUNK_SIZE;
    const baseZ = cz * CHUNK_SIZE;
    function neighborAt(x: number, y: number, z: number): number {
      if (
        x >= 0 && x < CHUNK_SIZE &&
        y >= 0 && y < CHUNK_SIZE &&
        z >= 0 && z < CHUNK_SIZE
      ) {
        return blocks[x + z * CHUNK_SIZE + y * CHUNK_SIZE * CHUNK_SIZE];
      }
      return worldAccess.getBlockAtWorld(baseX + x, baseY + y, baseZ + z);
    }

    for (let x = 0; x < CHUNK_SIZE; x++) {
      for (let y = 0; y < CHUNK_SIZE; y++) {
        for (let z = 0; z < CHUNK_SIZE; z++) {
          const idx = x + z * CHUNK_SIZE + y * CHUNK_SIZE * CHUNK_SIZE;
          const blockId = blocks[idx];
          if (blockId === BlockType.AIR) continue;

          const isWater = blockId === BlockType.WATER;

          for (const face of faces) {
            const [dx, dy, dz] = face.dir;
            const neighborBlock = neighborAt(x + dx, y + dy, z + dz);

            if (isWater) {
              // Water only shows faces against AIR (its surface) —
              // never against solids or other water (no inner walls)
              if (neighborBlock !== BlockType.AIR) continue;
            } else {
              if (!isBlockTransparent(neighborBlock) && neighborBlock !== BlockType.AIR) continue;
              if (neighborBlock === blockId) continue;
            }

            const target = isWater ? water : solid;
            const vertexOffset = target.positions.length / 3;

            // UV coordinates from atlas
            const faceUVs = atlas.getBlockFaceUVs(blockId, face.faceType, face.uvs);

            // Directional shade (vertex colors as white * shade)
            const shade = face.normal[1] === 1 ? 1.0 : face.normal[1] === -1 ? 0.6 : 0.8;

            for (let vi = 0; vi < 4; vi++) {
              const vert = face.verts[vi];
              // Water surface sits slightly below the block top (Minecraft feel)
              const yOffset = isWater && vert[1] === 1 ? -0.125 : 0;
              target.positions.push(
                cx * CHUNK_SIZE + x + vert[0],
                cy * CHUNK_SIZE + y + vert[1] + yOffset,
                cz * CHUNK_SIZE + z + vert[2]
              );
              target.normals.push(face.normal[0], face.normal[1], face.normal[2]);
              target.uvs.push(faceUVs[vi * 2], faceUVs[vi * 2 + 1]);
              target.colors.push(shade, shade, shade);
            }

            target.indices.push(
              vertexOffset, vertexOffset + 1, vertexOffset + 2,
              vertexOffset, vertexOffset + 2, vertexOffset + 3
            );
          }
        }
      }
    }

    return {
      solid: buffersToMesh(solid, solidMaterial),
      water: buffersToMesh(water, waterMaterial),
    };
  }

  // ─── Game loop ───

  let inputSendTimer = 0;
  const INPUT_SEND_INTERVAL = 0.05; // 20 Hz, matches server tick rate

  const UNDERWATER_FOG_COLOR = 0x1a4a8a;

  // Fog warm-up: right after joining only ~3 chunks are loaded while the
  // background loader fills out to the full render distance — the fog
  // grows with the estimated loaded radius so the void is never visible.
  let fogWarmup = 0; // seconds since world start
  let currentIsNight = false; // set each frame by updateDayNight (drives mob spawns)

  function isCameraUnderwater(): boolean {
    // Base this on the PLAYER's head, not the free camera — in third
    // person the pulled-back camera can dip into a nearby water block and
    // tint the whole screen blue even while the player is on dry land.
    const ps = playerController.state;
    const eyeY = ps.y + PLAYER_EYE_HEIGHT;
    return (
      worldAccess.getBlockAtWorld(Math.floor(ps.x), Math.floor(eyeY), Math.floor(ps.z)) ===
      BlockType.WATER
    );
  }

  function updateDayNight(dt: number) {
    let timeOfDay: number;
    if (network?.isConnected()) {
      timeOfDay = network.getTimeOfDay();
    } else {
      localTimeOfDay = (localTimeOfDay + dt / OFFLINE_DAY_LENGTH) % 1;
      timeOfDay = localTimeOfDay;
    }
    const dayNight = getDayNight(timeOfDay);
    currentIsNight = dayNight.isNight;
    const fog = engine.scene.fog as THREE.Fog | null;

    if (isCameraUnderwater()) {
      // Dense blue fog underwater — no more "inside a box" feeling
      const depthColor = new THREE.Color(UNDERWATER_FOG_COLOR).multiplyScalar(
        Math.max(0.35, dayNight.ambientIntensity + 0.2)
      );
      (engine.scene.background as THREE.Color).copy(depthColor);
      if (fog) {
        fog.color.copy(depthColor);
        fog.near = 1;
        fog.far = 22;
      }
    } else {
      (engine.scene.background as THREE.Color).setHex(dayNight.skyColor);
      if (fog) {
        fog.color.setHex(dayNight.fogColor);
        // Fog must close BEFORE the edge of loaded chunks — otherwise
        // the player sees the void past the last chunk ("world in a box")
        fogWarmup += dt;
        const loadedRadius = Math.min(renderDistance, 3 + fogWarmup * 1.2);
        const worldEdge = loadedRadius * CHUNK_SIZE;
        fog.near = worldEdge * 0.55;
        fog.far = worldEdge * 0.92;
      }
    }

    engine.ambientLight.intensity = dayNight.ambientIntensity;
    engine.directionalLight.intensity = dayNight.sunIntensity;

    const ps = playerController.state;
    engine.directionalLight.position.set(
      ps.x + Math.cos(dayNight.sunAngle) * 150,
      Math.max(20, Math.sin(dayNight.sunAngle) * 200),
      ps.z + 50
    );
  }

  function update(dt: number) {
    // Multiplayer sync + ambient visuals keep running while paused/in
    // menus — the world doesn't pause for other players.
    remotePlayers.update(dt);
    blockParticles.update(dt);
    updateDayNight(dt);
    if (network?.isConnected()) {
      inputSendTimer += dt;
      if (inputSendTimer >= INPUT_SEND_INTERVAL) {
        inputSendTimer = 0;
        const s = playerController.state;
        network.sendInput({ x: s.x, y: s.y, z: s.z, yaw: s.yaw, pitch: s.pitch });
      }
    }

    if (paused || inventoryOpen || chestOpen || isDead || chat.isOpen()) {
      // Discard stale input state so nothing "fires" when we resume:
      // mouse deltas accumulated behind an overlay would snap the camera,
      // and a click during chat would register as a fresh attack edge.
      input.resetMouseDelta();
      prevAttack = input.state.attack;
      prevInteract = input.state.interact;
      crosshairEl?.classList.remove("on-target");
      return;
    }

    // Player movement + collision (touch devices have no pointer lock —
    // their look deltas come from the touch controls)
    playerController.update(dt, input.state, input.isPointerLocked || touch);
    input.resetMouseDelta();

    // Fall damage (accumulated in player-controller)
    const fallDmg = playerController.consumeFallDamage();
    if (fallDmg > 0) applyDamage(fallDmg);

    const ps = playerController.state;
    // Interactions always originate at the player's EYES — in third
    // person the camera sits elsewhere.
    const eyeX = ps.x;
    const eyeY = ps.y + PLAYER_EYE_HEIGHT;
    const eyeZ = ps.z;

    // Chunk loading
    chunkLoader.update(ps.x, ps.z);

    // Raycast
    const dir = getCameraDirection(ps.yaw, ps.pitch);
    const hit = raycastVoxels(
      eyeX, eyeY, eyeZ,
      dir.x, dir.y, dir.z,
      BLOCK_BREAK_REACH, worldAccess
    );

    blockHighlight.update(hit);

    // Right-click priority: use block (bed/table) → eat → place.
    // Whatever consumes the click suppresses block placement.
    const interactJustPressed = input.state.interact && !prevInteract;
    prevInteract = input.state.interact;
    let interactConsumed = false;

    if (interactJustPressed && hit) {
      const targetBlock = worldAccess.getBlockAtWorld(hit.blockX, hit.blockY, hit.blockZ);
      if (targetBlock === BlockType.BED) {
        interactConsumed = true;
        sleepInBed();
      } else if (targetBlock === BlockType.CRAFTING_TABLE) {
        interactConsumed = true;
        inventoryScreen.showCraftTab();
      } else if (targetBlock === BlockType.CHEST) {
        interactConsumed = true;
        chestScreen.open(chestStore.getChest(hit.blockX, hit.blockY, hit.blockZ), inventory);
      } else if (targetBlock === BlockType.FURNACE) {
        interactConsumed = true;
        inventoryScreen.showCraftTab(); // smelting recipes unlock while near it
      }
    }

    if (interactJustPressed && !interactConsumed) {
      const selected = hotbar.getSelectedBlock();
      const food = FOOD_DEFS[selected];
      if (food && hungerState.hunger < hungerState.maxHunger) {
        interactConsumed = true;
        inventory.removeItem(selected, 1);
        addFood(hungerState, food.hunger, food.saturation);
        sfx.playEat();
        firstPersonArm.swing();
        localModel.swingArm();
        hotbar.refresh();
        statusBars.updateHunger(hungerState.hunger, hungerState.maxHunger);
        fireQuestEvent("eat", selected);
      }
    }

    blockInteraction.update(hit, input.state, dt, interactConsumed);

    // Aimed mob (shared by melee attack + crosshair feedback)
    let aimedMob = null;
    for (const mob of mobSystem.mobs) {
      if (mob.dead) continue;
      const mdx = mob.x - eyeX;
      const mdy = (mob.y + getMobStats(mob.type).height / 2) - eyeY;
      const mdz = mob.z - eyeZ;
      const mobDist = Math.sqrt(mdx * mdx + mdy * mdy + mdz * mdz);
      if (mobDist > BLOCK_BREAK_REACH) continue;

      const dot = (dir.x * mdx + dir.y * mdy + dir.z * mdz) / mobDist;
      if (dot > 0.9) { // ~25 degree cone
        aimedMob = mob;
        break;
      }
    }

    // PvP: nearest remote player in the aim cone within reach
    let aimedPlayer: { sessionId: string; dist: number } | null = null;
    if (network?.isConnected()) {
      for (const p of remotePlayers.getTargets()) {
        const pdx = p.x - eyeX;
        const pdy = p.y + 0.9 - eyeY;
        const pdz = p.z - eyeZ;
        const pdist = Math.sqrt(pdx * pdx + pdy * pdy + pdz * pdz);
        if (pdist > BLOCK_BREAK_REACH) continue;
        const dot = (dir.x * pdx + dir.y * pdy + dir.z * pdz) / pdist;
        if (dot > 0.9 && (!aimedPlayer || pdist < aimedPlayer.dist)) {
          aimedPlayer = { sessionId: p.sessionId, dist: pdist };
        }
      }
    }
    crosshairEl?.classList.toggle("on-target", aimedMob !== null || aimedPlayer !== null);

    // Arm swings while attacking (Minecraft-style hand animation)
    armSwingCooldown -= dt;
    if (input.state.attack && armSwingCooldown <= 0) {
      armSwingCooldown = 0.25;
      firstPersonArm.swing();
      localModel.swingArm();
      sfx.playSwing();
    }

    // Melee (left click leading edge) — swords hit harder. Players take
    // priority over mobs when both are aimed and the player is closer.
    const attackJustPressed = input.state.attack && !prevAttack;
    prevAttack = input.state.attack;
    if (attackJustPressed) {
      const meleeDmg = getMeleeDamage(hotbar.getSelectedBlock());
      const mobDist = aimedMob
        ? Math.hypot(aimedMob.x - eyeX, aimedMob.y - eyeY, aimedMob.z - eyeZ)
        : Infinity;
      if (aimedPlayer && aimedPlayer.dist <= mobDist) {
        network?.sendHit(aimedPlayer.sessionId, meleeDmg); // PvP
        sfx.playHit();
      } else if (aimedMob) {
        const killed = damageMob(aimedMob, meleeDmg);
        sfx.playHit();
        if (killed) handleMobKilled(aimedMob);
      }
    }

    // Abilities
    abilityBar.setPosition({ x: ps.x, y: ps.y, z: ps.z });
    abilityBar.setLookDirection(dir);
    abilityBar.update(dt);

    // Check for ability cast → spawn visual effect + apply REAL damage
    const lastCast = abilityBar.getLastCast();
    if (lastCast && lastCast.success) {
      abilityEffects.spawn(lastCast);
      firstPersonArm.swing();
      localModel.swingArm();

      if (lastCast.ability && lastCast.ability.damage > 0) {
        const targets: AbilityTarget[] = [];
        for (const mob of mobSystem.mobs) {
          if (mob.dead) continue;
          targets.push({
            id: mob.id,
            x: mob.x,
            y: mob.y,
            z: mob.z,
            radius: 0.6,
            height: getMobStats(mob.type).height,
          });
        }
        const origin = { x: eyeX, y: eyeY, z: eyeZ };
        const hits = computeAbilityHits(lastCast.ability, origin, dir, targets);
        for (const abilityHit of hits) {
          const mob = mobSystem.mobs.find((m) => m.id === abilityHit.targetId);
          if (!mob || mob.dead) continue;
          sfx.playHit();
          const killed = damageMob(mob, abilityHit.damage);
          if (killed) handleMobKilled(mob);
        }

        // PvP: abilities also hit remote players
        if (network?.isConnected()) {
          const players = remotePlayers.getTargets();
          const playerTargets: AbilityTarget[] = players.map((p, i) => ({
            id: i, x: p.x, y: p.y, z: p.z, radius: 0.5, height: 1.8,
          }));
          for (const h of computeAbilityHits(lastCast.ability, origin, dir, playerTargets)) {
            network.sendHit(players[h.targetId].sessionId, lastCast.ability.damage);
            sfx.playHit();
          }
        }
      }
    }

    // Update active ability effects
    abilityEffects.update(dt);

    // Mobs — melee needs proximity; skeletons shoot visible arrows
    const mobResult = mobSystem.update(dt, ps.x, ps.y, ps.z, currentIsNight);
    if (!creative) {
      for (const evt of mobResult.damageEvents) {
        applyDamage(evt.damage);
        playerController.applyKnockback(evt.knockbackDirX, evt.knockbackDirZ);
        sfx.playHit();
      }
      for (const shot of mobResult.shootEvents) {
        mobArrows.spawn(shot);
      }
    }
    const arrowHits = mobArrows.update(dt, ps.x, ps.y, ps.z);
    for (const hitInfo of arrowHits) {
      applyDamage(hitInfo.damage);
      playerController.applyKnockback(hitInfo.knockbackDirX, hitInfo.knockbackDirZ);
      sfx.playHit();
    }
    mobRenderer.syncMobs(mobSystem.mobs, engine.camera);

    // Dropped items — auto pickup on proximity (full inventory leaves
    // the drop on the ground instead of destroying it)
    const pickedItems = droppedItems.update(dt, ps.x, ps.y, ps.z, (itemId, count) =>
      inventory.canFit(itemId, count)
    );
    if (pickedItems.length > 0) {
      for (const item of pickedItems) {
        inventory.addItem(item.itemId, item.count);
      }
      sfx.playPlace(); // short pop
      hotbar.refresh();
    }

    // Breaking cracks overlay follows the current break progress
    blockCracks.update(blockInteraction.getBreakProgress());

    // Hunger (not in creative) — actual sprinting drains it faster
    if (!creative) {
      const starvationDmg = updateHunger(hungerState, dt, ps.sprinting);
      if (starvationDmg > 0) applyDamage(starvationDmg);

      // Regenerate health while well-fed (hunger ≥ 18)
      const heal = computeRegen(hungerState, dt, abilityBar.state.health, abilityBar.state.maxHealth);
      if (heal > 0 && !isDead) {
        abilityBar.state.health = Math.min(abilityBar.state.maxHealth, abilityBar.state.health + heal);
      }
    }

    // Update status bars
    statusBars.updateHealth(abilityBar.state.health, abilityBar.state.maxHealth);
    statusBars.updateHunger(hungerState.hunger, hungerState.maxHunger);

    // Sprint FOV kick — running FEELS like running
    const targetFov = ps.sprinting ? 82 : 75;
    if (Math.abs(engine.camera.fov - targetFov) > 0.05) {
      engine.camera.fov = lerp(engine.camera.fov, targetFov, dampFactor(8, dt));
      engine.camera.updateProjectionMatrix();
    }

    // First-person arm + local third-person body + minimap
    const horizontalSpeed = Math.sqrt(ps.vx * ps.vx + ps.vz * ps.vz);
    firstPersonArm.setHeldItem(hotbar.getSelectedBlock());
    firstPersonArm.setVisible(cameraRig.isFirstPerson());
    firstPersonArm.update(dt, horizontalSpeed);

    localModel.group.visible = !cameraRig.isFirstPerson();
    if (localModel.group.visible) {
      localModel.group.position.set(ps.x, ps.y, ps.z);
      localModel.group.rotation.y = ps.yaw;
      localModel.update(dt, horizontalSpeed, ps.inWater);
    }

    minimap.update(dt, ps.x, ps.y, ps.z, ps.yaw, remotePlayers.getPositions());

    // FPS
    frameCount++;
    fpsTimer += dt;
    if (fpsTimer >= 1) {
      currentFps = frameCount;
      frameCount = 0;
      fpsTimer = 0;
    }
  }

  function render(_alpha: number) {
    const ps = playerController.state;
    cameraRig.update(engine.camera, ps);
    engine.renderer.render(engine.scene, engine.camera);

    if (debugEl) {
      debugEl.textContent = [
        `FPS: ${currentFps}`,
        `XYZ: ${ps.x.toFixed(1)} / ${ps.y.toFixed(1)} / ${ps.z.toFixed(1)}`,
        `Chunks: ${chunks.size}`,
        `Level: ${progression.level} | XP: ${progression.xp}/${progression.xpToNextLevel}`,
        network?.isConnected()
          ? `Online: ${remotePlayers.count() + 1} player(s)`
          : "Offline (single-player)",
      ].join("\n");
    }
  }

  const gameLoop = createGameLoop({ update, render });

  // Music on first interaction
  function startMusicOnInteraction() {
    music.start();
    document.removeEventListener("click", startMusicOnInteraction);
    document.removeEventListener("keydown", startMusicOnInteraction);
  }
  document.addEventListener("click", startMusicOnInteraction);
  document.addEventListener("keydown", startMusicOnInteraction);

  return {
    start() {
      gameLoop.start();
      const loadingEl = document.getElementById("loading-screen");
      if (loadingEl) loadingEl.classList.add("hidden");
    },
    stop() {
      gameLoop.stop();
    },
    dispose() {
      gameLoop.stop();
      music.stop();
      input.dispose();
      hotbar.dispose();
      abilityBar.dispose();
      xpBar.dispose();
      statusBars.dispose();
      pauseMenu.dispose();
      inventoryScreen.dispose();
      deathScreen.dispose();
      damageOverlay.dispose();
      abilityEffects.dispose();
      mobSystem.dispose();
      mobRenderer.dispose();
      remotePlayers.dispose();
      sfx.dispose();
      blockParticles.dispose();
      mobArrows.dispose();
      droppedItems.dispose();
      blockCracks.dispose();
      cameraRig.dispose();
      firstPersonArm.dispose();
      engine.scene.remove(localModel.group);
      localModel.dispose();
      minimap.dispose();
      chat.dispose();
      touchControls?.dispose();
      controlsHint?.dispose();
      questPanel.dispose();
      chestScreen.dispose();
      network?.disconnect();
      blockHighlight.dispose();
      blockInteraction.dispose();
      engine.dispose();
      document.removeEventListener("pointerlockchange", handlePointerLockChange);
      document.removeEventListener("keydown", handleUiKeys);
      chunks.forEach((chunk) => {
        chunk.mesh?.geometry.dispose();
        chunk.waterMesh?.geometry.dispose();
      });
      chunks.clear();
      solidMaterial.dispose();
      waterMaterial.dispose();
    },
  };
}
